"use client";

import Image, { StaticImageData } from "next/image";
import React from "react";

export type BenefitItem = {
  title: string[] | string;
  description: string;
  icon?: string | StaticImageData;
};

export type IndustryBenefitGridCardProps = {
  item: BenefitItem;
  index: number;
  /** Controls the color scheme of the card. Defaults to "dark". */
  theme?: "dark" | "light";
};

export default function IndustryBenefitGridCard({
  item,
  index,
  theme = "dark",
}: IndustryBenefitGridCardProps) {
  const title = Array.isArray(item.title) ? item.title.join(" ") : item.title;
  const isDark = theme === "dark";

  return (
    <article className="relative flex flex-col self-start outline-none group px-1">
      {/* Ambient glow on hover */}
      <div
        className={`pointer-events-none absolute -inset-6 rounded-3xl blur-2xl transition-all duration-700 ${
          isDark ? "bg-[#225BD4]/0 group-hover:bg-[#225BD4]/[0.06]" : ""
        }`}
      />

      {/* Icon + Title on one line */}
      <div className="relative flex items-center gap-4">
        {item.icon && (
          <div
            className={`relative rounded-lg flex items-center justify-center w-[60px] h-[60px] shrink-0 overflow-hidden backdrop-blur-sm transition-all duration-500 group-hover:-translate-y-0.5 border ${
              isDark
                ? "border-white/20 bg-white/[0.02] group-hover:border-[#225BD4]/50 group-hover:bg-[#225BD4]/[0.1]"
                : "border-[#B0BFD0] bg-white/70 group-hover:border-[#1A4DB8]/50 group-hover:bg-white/90"
            }`}
          >
            <div
              className={`absolute inset-0 rounded-lg scale-0 transition-transform duration-500 ease-out group-hover:scale-100 ${
                isDark
                  ? "bg-gradient-to-br from-[#225BD4]/20 to-transparent"
                  : "bg-gradient-to-br from-[#1A4DB8]/10 to-transparent"
              }`}
            />
            <Image
              src={item.icon}
              alt=""
              width={32}
              height={32}
              className="relative z-10 w-[34px] h-[34px] object-contain transition-transform duration-500 group-hover:scale-110"
            />
          </div>
        )}

        <h3
          className={`clash-font text-[length:var(--space-20-32)] font-[500] leading-[1.15] tracking-tight transition-colors duration-300 ${
            isDark
              ? "text-white group-hover:text-white"
              : "text-[#0F172A] group-hover:text-[#1A4DB8]"
          }`}
          dangerouslySetInnerHTML={{ __html: title ?? "" }}
        />
      </div>

      {/* Divider */}
      <div
        className={`relative mt-6 h-px w-full overflow-hidden ${
          isDark
            ? "bg-gradient-to-r from-white/15 via-white/10 to-transparent"
            : "bg-gradient-to-r from-[#9DB4CA] via-[#9DB4CA]/50 to-transparent"
        }`}
      >
        <div className="absolute inset-y-0 left-0 w-0 bg-gradient-to-r from-[#1A4DB8] to-[#1A4DB8]/40 transition-all duration-700 ease-out group-hover:w-full" />
      </div>

      {/* Description */}
      <p
        className={`mt-5 leading-relaxed text-[length:var(--primarybtn)] transition-colors duration-300 ${
          isDark
            ? "text-white/55 group-hover:text-white/70"
            : "text-[#334155] group-hover:text-[#0F172A]"
        }`}
        dangerouslySetInnerHTML={{ __html: item.description }}
      />
    </article>
  );
}
